import { useEffect, useRef, useState } from 'react';
import { txt } from '../i18n';
import './modal.css';

// Popup con el mismo lenguaje visual del HUD (marco de esquinas, scanlines,
// dorado y rojo). Se cierra con Escape, con el boton, o clickeando el fondo.
// Es un dialogo accesible: recibe el foco al abrirse, lo mantiene dentro
// (Tab cicla entre sus controles) y lo devuelve al elemento que lo abrio.
//
// "paginas" (opcional) divide el contenido en varias pantallas con
// navegacion al pie, para que un briefing largo no obligue a hacer scroll;
// sin ella se renderiza children como unica pagina.
export default function Modal({ titulo, codigo, onClose, paginas, children }) {
  const panelRef = useRef(null);
  const [pagina, setPagina] = useState(0);
  const conPaginas = Array.isArray(paginas) && paginas.length > 0;
  const ultima = conPaginas && pagina === paginas.length - 1;

  useEffect(() => {
    const disparador = document.activeElement;
    panelRef.current?.focus();

    function alPresionar(e) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focos = panelRef.current.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focos.length === 0) return;
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    }
    window.addEventListener('keydown', alPresionar);
    return () => {
      window.removeEventListener('keydown', alPresionar);
      if (disparador instanceof HTMLElement) disparador.focus();
    };
  }, [onClose]);

  return (
    <div className="modal-fondo" onClick={onClose}>
      <div
        className="modal-panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof titulo === 'string' ? titulo : txt('modal.explicacion')}
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-scanlines" />
        <div className="modal-contenido">
          <header className="modal-encabezado">
            <div className="modal-titulos">
              {codigo && <span className="modal-codigo">{codigo}</span>}
              <span className="modal-titulo">{titulo}</span>
            </div>
            <button type="button" className="modal-cerrar" onClick={onClose} aria-label={txt('modal.cerrar')}>
              ✕
            </button>
          </header>
          <div className="modal-cuerpo">{conPaginas ? paginas[pagina] : children}</div>
          {conPaginas && paginas.length > 1 && (
            <footer className="modal-paginacion">
              <button
                type="button"
                className="modal-nav"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => p - 1)}
              >
                {txt('modal.anterior')}
              </button>
              <span className="modal-paginacion-indicador">
                {String(pagina + 1).padStart(2, '0')} / {String(paginas.length).padStart(2, '0')}
              </span>
              {ultima ? (
                <button type="button" className="modal-nav modal-nav--principal" onClick={onClose}>
                  {txt('modal.iniciar')}
                </button>
              ) : (
                <button
                  type="button"
                  className="modal-nav"
                  onClick={() => setPagina((p) => p + 1)}
                >
                  {txt('modal.siguiente')}
                </button>
              )}
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}

// Boton pequeno que abre un popup. Se ubica junto a los titulos de seccion.
// `etiqueta` describe que explica el popup y llega ya traducida.
export function BotonInfo({ onClick, etiqueta = txt('modal.explicacion') }) {
  return (
    <button
      type="button"
      className="boton-info"
      onClick={onClick}
      aria-label={etiqueta}
      title={etiqueta}
    >
      ?
    </button>
  );
}
